import { useNavigate } from "react-router";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/shared/empty-state";
import { useGetApiWorkflowDefinitions } from "@/lib/api/generated/workflows/workflows";
import { useGetApiWorkflowPolicies } from "@/lib/api/generated/workflows/workflows";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import type {
  WorkflowDefinition,
  WorkflowPolicyRecord,
} from "../../../../../packages/shared-types/src/index.js";

export function WorkflowPoliciesPage() {
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const { data: definitions } = useGetApiWorkflowDefinitions<WorkflowDefinition[]>({
    query: { enabled: Boolean(tenantId) },
  });
  const { data: policies } = useGetApiWorkflowPolicies<WorkflowPolicyRecord[]>({
    query: { enabled: Boolean(tenantId) },
  });
  useDocumentTitle("Workflows");

  return (
    <div className="flex flex-col gap-6">
      {/* Workflow catalog */}
      <Card>
        <CardHeader>
            <CardTitle>Governance Catalog</CardTitle>
            <p className="text-xs max-w-2xl" style={{ color: "var(--muted-foreground)" }}>
              This screen is not the automation editor. It governs built-in workflow definitions
              and tenant-level access policy.
            </p>
        </CardHeader>
        <CardContent>
          {!definitions || definitions.length === 0 ? (
            <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>No workflow definitions.</p>
          ) : (
            <div className="border border-border rounded-[var(--radius-sm)] overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ background: "var(--bg-accent)" }}>
                    <th className="text-left px-3 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>Key</th>
                    <th className="text-left px-3 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>Name</th>
                    <th className="text-left px-3 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>Side Effect</th>
                  </tr>
                </thead>
                <tbody>
                  {definitions.map((def) => (
                    <tr key={def.workflowKey} className="border-t border-border">
                      <td className="px-3 py-2 font-mono">{def.workflowKey}</td>
                      <td className="px-3 py-2">{def.name}</td>
                      <td className="px-3 py-2"><Badge variant="outline">{def.sideEffectClass}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tenant policies */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex flex-col gap-1">
            <CardTitle>Governance Policies</CardTitle>
            <p className="text-xs max-w-2xl" style={{ color: "var(--muted-foreground)" }}>
              Use Automations for prompt-first scheduled work. Use governance policies only when
              you need to enable or constrain built-in workflow behavior for this tenant.
            </p>
          </div>
          <Button size="sm" onClick={() => navigate("/workspace/workflows/new")}>
            <Plus size={14} />
            Add Governance Policy
          </Button>
        </CardHeader>
        <CardContent>
          {!policies || policies.length === 0 ? (
            <EmptyState title="No workflow policies" description="Enable workflows for this tenant." />
          ) : (
            <div className="border border-border rounded-[var(--radius-sm)] overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ background: "var(--bg-accent)" }}>
                    <th className="text-left px-3 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>Workflow</th>
                    <th className="text-left px-3 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>Enabled</th>
                    <th className="text-left px-3 py-2 font-medium" style={{ color: "var(--muted-foreground)" }}>Channels</th>
                  </tr>
                </thead>
                <tbody>
                  {policies.map((p) => (
                    <tr
                      key={p.workflowKey}
                      className="border-t border-border cursor-pointer hover:bg-[var(--bg-hover)] transition-colors"
                      onClick={() => navigate(`/workspace/workflows/${p.workflowKey}`)}
                    >
                      <td className="px-3 py-2 font-mono">{p.workflowKey}</td>
                      <td className="px-3 py-2">
                        <Badge variant={p.enabled ? "active" : "draft"}>{p.enabled ? "enabled" : "disabled"}</Badge>
                      </td>
                      <td className="px-3 py-2" style={{ color: "var(--muted-foreground)" }}>
                        {p.allowedChannels.join(", ") || "all"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
